import { useEffect, useState, type ReactNode } from 'react';
import { fmt } from '../../lib/format';

/* Instrumentos do HUD. Todos desenham só o que recebem: valor ausente (null)
   aparece apagado, nunca como zero inventado. */

const TAU = Math.PI * 2;
const polar = (cx: number, cy: number, r: number, a: number) => [cx + r * Math.cos(a), cy + r * Math.sin(a)] as const;

function arcPath(cx: number, cy: number, r: number, a0: number, a1: number) {
  const [x0, y0] = polar(cx, cy, r, a0);
  const [x1, y1] = polar(cx, cy, r, a1);
  return `M${x0.toFixed(2)} ${y0.toFixed(2)}A${r} ${r} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}

function slicePath(cx: number, cy: number, r: number, a0: number, a1: number) {
  const [x0, y0] = polar(cx, cy, r, a0);
  const [x1, y1] = polar(cx, cy, r, a1);
  return `M${cx} ${cy}L${x0.toFixed(2)} ${y0.toFixed(2)}A${r} ${r} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}Z`;
}

/* Painel com título, colchetes nos cantos e linha de conexão. */
export function Panel({ title, tag, className = '', children, href, label }: {
  title: string; tag?: ReactNode; className?: string; children: ReactNode; href?: string; label?: string;
}) {
  const head = (
    <header className="hp-head">
      <span className="hp-title">{title}</span>
      {tag != null && <span className="hp-tag">{tag}</span>}
    </header>
  );
  return (
    <section className={`hx-w hp ${className}`} aria-label={label ?? title}>
      {href ? <a className="hp-link" href={href}>{head}</a> : head}
      {children}
    </section>
  );
}

/* Medidor circular: trilho, escala, arco do valor e anel interno girando. */
export function RingGauge({ value, size = 120, center, caption }: {
  value: number | null; size?: number; center: ReactNode; caption?: string;
}) {
  const c = 60, r = 46;
  const v = value == null ? 0 : Math.max(0, Math.min(100, value));
  const a0 = -Math.PI / 2;
  return (
    <div className={`rg ${value == null ? 'off' : ''}`} style={{ width: size, height: size }}>
      <svg viewBox="0 0 120 120" aria-hidden="true">
        <defs>
          <linearGradient id="rg-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#d6f4ff" /><stop offset="1" stopColor="#1a7cf0" />
          </linearGradient>
        </defs>
        <circle className="rg-track" cx={c} cy={c} r={r} />
        <g className="rg-ticks">
          {Array.from({ length: 60 }, (_, i) => {
            const a = (i / 60) * TAU;
            const [x0, y0] = polar(c, c, 53, a);
            const [x1, y1] = polar(c, c, i % 5 === 0 ? 58 : 56, a);
            return <line key={i} x1={x0} y1={y0} x2={x1} y2={y1} />;
          })}
        </g>
        {v > 0 && <path className="rg-val" d={arcPath(c, c, r, a0, a0 + (Math.min(v, 99.9) / 100) * TAU)} />}
        <g className="rg-spin"><circle className="rg-inner" cx={c} cy={c} r={36} /></g>
        <circle className="rg-core" cx={c} cy={c} r={30} />
      </svg>
      <div className="rg-center">{center}</div>
      {caption && <span className="rg-cap">{caption}</span>}
    </div>
  );
}

/* Pizza com uma fatia destacada, como na referência. */
export function Pie({ slices, size = 120 }: { slices: { on: boolean; label: string }[]; size?: number }) {
  const n = slices.length;
  const firstOn = slices.findIndex((s) => s.on);
  return (
    <svg className="pie" viewBox="0 0 120 120" width={size} height={size} role="img"
      aria-label={`${slices.filter((s) => s.on).length} de ${n} ativos`}>
      <circle className="pie-bg" cx="60" cy="60" r="50" />
      {slices.map((s, i) => {
        const a0 = -Math.PI / 2 + (i / n) * TAU + 0.02;
        const a1 = -Math.PI / 2 + ((i + 1) / n) * TAU - 0.02;
        const mid = (a0 + a1) / 2;
        const off = i === firstOn ? 7 : 0;
        return (
          <path key={s.label} className={`pie-sl ${s.on ? 'on' : ''}`}
            transform={`translate(${(Math.cos(mid) * off).toFixed(2)} ${(Math.sin(mid) * off).toFixed(2)})`}
            d={slicePath(60, 60, s.on ? 48 : 44, a0, a1)}><title>{s.label}</title></path>
        );
      })}
      <circle className="pie-hole" cx="60" cy="60" r="14" />
    </svg>
  );
}

/* Pizza pequena: progresso de 0 a 100. */
export function MiniPie({ value, label, sub }: { value: number | null; label: string; sub?: string }) {
  const v = value == null ? 0 : Math.max(0, Math.min(100, value));
  const a0 = -Math.PI / 2;
  return (
    <figure className={`mpie ${value == null ? 'off' : ''}`}>
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <circle className="mpie-bg" cx="24" cy="24" r="20" />
        {v >= 99.9 ? <circle className="mpie-fill" cx="24" cy="24" r="17" />
          : v > 0 && <path className="mpie-fill" d={slicePath(24, 24, 17, a0, a0 + (v / 100) * TAU)} />}
        <circle className="mpie-ring" cx="24" cy="24" r="22" />
      </svg>
      <figcaption><b>{label}</b>{sub && <span>{sub}</span>}</figcaption>
    </figure>
  );
}

/* Barras horizontais com régua. */
export function HBars({ rows }: { rows: { label: string; value: number | null; text: string }[] }) {
  return (
    <div className="hbars">
      {rows.map((r) => (
        <div key={r.label} className={`hbar ${r.value == null ? 'off' : ''}`}>
          <div className="hbar-top"><span>{r.label}</span><b>{r.text}</b></div>
          <div className="hbar-track"><i style={{ width: `${Math.max(0, Math.min(100, (r.value ?? 0) * 100))}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

/* Colunas verticais (equalizador, distribuição). Valores de 0 a 1. */
export function VBars({ values, className = '', live = false }: { values: number[]; className?: string; live?: boolean }) {
  return (
    <div className={`vbars ${live ? 'live' : ''} ${className}`} aria-hidden="true">
      {values.map((v, i) => <i key={i} style={{ height: `${Math.max(4, Math.min(100, v * 100))}%` }} />)}
    </div>
  );
}

/* Linha com área preenchida. */
export function LineChart({ values, w = 300, h = 70 }: { values: number[]; w?: number; h?: number }) {
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? w / (values.length - 1) : w;
  const pts = values.map((v, i) => [i * step, h - 6 - (v / max) * (h - 14)] as const);
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join('');
  return (
    <svg className="lchart" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id="lc-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgb(63 210 255 / 45%)" /><stop offset="1" stopColor="rgb(63 210 255 / 0%)" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((f) => <line key={f} className="lc-grid" x1="0" x2={w} y1={h * f} y2={h * f} />)}
      {pts.length > 1 && <path className="lc-area" d={`${line}L${w} ${h}L0 ${h}Z`} />}
      {pts.length > 1 && <path className="lc-line" d={line} />}
      {pts.map(([x, y], i) => values[i]! > 0 && <circle key={i} className="lc-dot" cx={x} cy={y} r="2.2" />)}
    </svg>
  );
}

/* Holograma triangular (a "vela" da referência) sobre um disco de luz. */
export function TriangleHolo({ dim = false }: { dim?: boolean }) {
  return (
    <svg className={`tri ${dim ? 'off' : ''}`} viewBox="0 0 160 130" aria-hidden="true">
      <defs>
        <linearGradient id="tri-a" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffffff" /><stop offset="1" stopColor="#3fd2ff" stopOpacity=".55" />
        </linearGradient>
        <linearGradient id="tri-b" x1="1" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#bfeaff" /><stop offset="1" stopColor="#1a7cf0" stopOpacity=".5" />
        </linearGradient>
        <radialGradient id="tri-disc">
          <stop offset="0" stopColor="#d6f4ff" stopOpacity=".9" /><stop offset=".5" stopColor="#3fd2ff" stopOpacity=".35" /><stop offset="1" stopColor="#3fd2ff" stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse cx="80" cy="104" rx="70" ry="17" fill="url(#tri-disc)" />
      <ellipse className="tri-ring" cx="80" cy="104" rx="56" ry="12" />
      <ellipse className="tri-ring r2" cx="80" cy="104" rx="38" ry="8" />
      <g className="tri-float">
        <path d="M80 10 L80 98 L42 98 Z" fill="url(#tri-a)" />
        <path d="M80 18 L80 98 L112 98 Z" fill="url(#tri-b)" />
        <path className="tri-edge" d="M80 10 L80 98 M80 10 L42 98 M80 18 L112 98" />
      </g>
    </svg>
  );
}

/* Ponto de estado com rótulo. */
export function Led({ on, label, title }: { on: boolean; label: string; title?: string }) {
  return (
    <span className={`led-c ${on ? 'on' : ''}`} title={title}>
      <i aria-hidden="true" /><span>{label}</span><span className="sr-only">{on ? 'ativo' : 'inativo'}</span>
    </span>
  );
}

export function useNow(ms: number) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

export function Clock() {
  const now = useNow(1000);
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  return (
    <div className="hx-w hx-clock" aria-label={`Agora: ${hh}:${mm}`}>
      <svg className="clk-dial" viewBox="0 0 60 60" aria-hidden="true">
        <circle cx="30" cy="30" r="26" />
        <path d={`M30 30 L${30 + 22 * Math.cos((now.getSeconds() / 60) * 6.283 - 1.571)} ${30 + 22 * Math.sin((now.getSeconds() / 60) * 6.283 - 1.571)}`} />
        <circle className="clk-dot" cx="30" cy="30" r="2.5" />
      </svg>
      <div>
        <b className="clk-time">{hh}:{mm}<small>{ss}</small></b>
        <span className="clk-date">{fmt.day(now)}</span>
      </div>
    </div>
  );
}
